import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useUser } from 'context/user-context';
import { GET_ASSISTED_INSTALLATION_QUEUE } from 'gql/queries';
import { UPDATE_ASSISTED_INSTALLATION_STATUS } from 'gql/mutations';
import { ASSISTANCE_LABELS, assistanceTransitions, assistanceUuid, readAssistanceQueue, readAssistanceRequest } from 'utils/assistedInstallation';
import type { AssistanceQueueRow, AssistanceStatus } from 'types/assistedInstallation';

export default function AssistedInstallationQueue() {
  const client = useApolloClient(); const { user } = useUser();
  const allowed = user?.defaultRole === 'shipaid_admin' && assistanceUuid(user.id);
  const scope = `${user?.id}|${allowed}`;
  const generation = useRef(0), activeScope = useRef(scope), lock = useRef(false);
  const activeClient = useRef(client); activeClient.current = client;
  activeScope.current = scope;
  const blank = { scope, client, rows: [] as AssistanceQueueRow[], drafts: {} as Record<string, AssistanceStatus>,
    loading: true, busy: false, needsReload: false, error: '', notice: '' };
  const [state, setState] = useState(blank);
  const visible = state.scope === scope && state.client === client ? state : blank;
  const read = useCallback(async () => {
    const result = await client.query({ query: GET_ASSISTED_INSTALLATION_QUEUE, fetchPolicy: 'no-cache',
      context: { queryDeduplication: false } });
    const rows = readAssistanceQueue(result.data?.assistedInstallationQueue);
    if (!rows) throw new Error('Unconfirmed assistance queue');
    return rows;
  }, [client]);
  const refresh = useCallback(async () => {
    if (!allowed || lock.current) return;
    lock.current = true; const sequence = ++generation.current;
    const current = () => activeClient.current === client && activeScope.current === scope && generation.current === sequence;
    setState(previous => ({ ...previous, scope, client, loading: true, busy: true, error: '', notice: '' }));
    try {
      const rows = await read(); if (!current()) return;
      setState(previous => ({ ...previous, scope, rows, drafts: {}, needsReload: false }));
    } catch {
      if (current()) setState(previous => ({ ...previous, rows: [], needsReload: true,
        error: 'The installation work queue could not be loaded. Try again.' }));
    } finally {
      if (current()) { lock.current = false; setState(previous => ({ ...previous, loading: false, busy: false })); }
    }
  }, [client, allowed, scope, read]);
  useEffect(() => {
    const scopeGeneration = generation; generation.current++; lock.current = false;
    setState({ scope, client, rows: [], drafts: {}, loading: allowed, busy: false, needsReload: false, error: '', notice: '' });
    void refresh(); return () => { scopeGeneration.current++; };
  }, [scope, client, allowed, refresh]);
  const update = async (baseline: AssistanceQueueRow, status: AssistanceStatus) => {
    if (!allowed || lock.current || visible.loading || visible.needsReload || status === baseline.status
      || !assistanceTransitions[baseline.status].includes(status) || (status === 'call_scheduled' && !baseline.callRequested)) return;
    lock.current = true; const sequence = ++generation.current;
    const current = () => activeClient.current === client && activeScope.current === scope && generation.current === sequence;
    setState(previous => ({ ...previous, busy: true, error: '', notice: '' }));
    try {
      const preflight = (await read()).find(row => row.id === baseline.id); if (!current()) return;
      if (!preflight || preflight.revision !== baseline.revision || preflight.storeId !== baseline.storeId
        || preflight.shopDomain !== baseline.shopDomain || preflight.status !== baseline.status || preflight.callRequested !== baseline.callRequested) {
        setState(previous => ({ ...previous, needsReload: true, error: 'The installation request changed. Reload before updating it; your pending status is preserved.' }));
        return;
      }
      const response = await client.mutate({ mutation: UPDATE_ASSISTED_INSTALLATION_STATUS,
        variables: { input: { requestId: baseline.id, revision: baseline.revision, status } } });
      if (!current()) return;
      const value = response.data?.updateAssistedInstallationStatus;
      const acknowledged = readAssistanceRequest(value?.request);
      if (value?.schemaVersion !== 1 || value.runtimeStatus !== 'unverified' || !acknowledged
        || acknowledged.id !== baseline.id || acknowledged.storeId !== baseline.storeId || acknowledged.shopDomain !== baseline.shopDomain
        || acknowledged.revision !== baseline.revision + 1 || acknowledged.status !== status) throw new Error('Unconfirmed assistance status');
      const rows = await read(); if (!current()) return;
      const saved = rows.find(row => row.id === baseline.id);
      const terminal = ['completed', 'cancelled'].includes(status);
      if (terminal ? !!saved : !saved || saved.storeId !== baseline.storeId || saved.shopDomain !== baseline.shopDomain
        || saved.revision < acknowledged.revision || saved.status !== status) throw new Error('Unconfirmed assistance readback');
      setState(previous => {
        const drafts = { ...previous.drafts }; delete drafts[baseline.id];
        return { ...previous, rows: previous.rows.flatMap(row => row.id === baseline.id ? saved ? [saved] : [] : [row]),
          drafts, notice: 'Assistance status updated.', needsReload: false };
      });
    } catch {
      if (current()) setState(previous => ({ ...previous, needsReload: true,
        error: 'The assistance update could not be confirmed. Reload the queue before trying again; your pending status is preserved.' }));
    } finally {
      if (current()) { lock.current = false; setState(previous => ({ ...previous, busy: false })); }
    }
  };
  if (!allowed) return null;
  const disabled = visible.busy || visible.loading || visible.needsReload;
  return <section aria-label="Assisted installation work queue" className="mx-auto mb-8 max-w-7xl rounded-lg border border-slate-200 p-4 sm:p-6">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="text-lg font-semibold">Assisted installation work queue</h2>
      <button type="button" disabled={visible.busy} className="text-sm text-blue-700 underline disabled:opacity-50"
        onClick={() => { void refresh(); }}>Reload assistance queue</button>
    </div>
    <p className="mt-2 text-sm text-slate-600">Arrange secure collaborator access and any requested call directly with the requester. Completing assistance does not verify storefront installation.</p>
    {visible.loading && <p role="status" className="mt-3 text-sm">Loading assistance requests…</p>}
    {visible.error && <p role="alert" className="mt-3 text-sm text-red-700">{visible.error}</p>}
    {visible.notice && <p role="status" className="mt-3 text-sm font-medium">{visible.notice}</p>}
    {!visible.loading && !visible.error && visible.rows.length === 0 && <p className="mt-3 text-sm">No active assistance requests.</p>}
    <div className="mt-4 space-y-4">{visible.rows.map(row => {
      const status = visible.drafts[row.id] ?? row.status;
      const choices = assistanceTransitions[row.status].filter(choice => choice !== 'call_scheduled' || row.callRequested);
      return <article key={row.id} className="min-w-0 rounded-lg bg-slate-50 p-4">
        <p className="break-all text-sm font-semibold">{row.shopDomain}</p><p className="mt-1 break-all text-sm">{row.contactEmail}</p>
        <p className="mt-1 break-all text-xs text-slate-500">Request {row.id}</p>
        <p className="mt-2 text-sm">{row.callRequested ? 'Call requested · Arrange separately' : 'No call requested'}</p>
        <p className="mt-1 text-xs">{row.notificationStatus === 'submitted' ? 'Notification submitted · Delivery unverified'
          : row.notificationStatus === 'queued' ? 'Notification queued' : 'Notification unconfirmed · Reconcile before any separate contact'}</p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-sm">Assistance status
            <select className="mt-1 block rounded-lg border border-slate-300 bg-white p-2 text-sm" disabled={disabled}
              value={status} onChange={event => {
                const selected = event.target.value as AssistanceStatus;
                setState(previous => ({ ...previous, drafts: { ...previous.drafts, [row.id]: selected } }));
              }}>
              <option value={row.status}>{ASSISTANCE_LABELS[row.status]}</option>
              {choices.map(choice => <option key={choice} value={choice}>{ASSISTANCE_LABELS[choice]}</option>)}
            </select>
          </label>
          <button type="button" disabled={disabled || status === row.status} className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            onClick={() => { void update(row, status); }}>Update assistance status</button>
        </div>
      </article>;
    })}</div>
  </section>;
}
